/**
 * Pure helpers for AgenticNetworkRoom's stream/run view. Extracted so the tricky bits — buffer
 * merge, the fresh-run "clear on Enter", and the one-run-at-a-time re-entry guard — are unit-testable
 * without rendering the whole room.
 */

/**
 * Merge the loaded history + live channel envelopes into ONE de-duplicated, chronological buffer
 * (history first, then live). De-duped by ``eventId`` (history + live overlap on reconnect).
 *
 * This buffer feeds the room reducer, so it is DELIBERATELY UNFILTERED — everything derived from it
 * (``uiState`` / ``currentRunId`` / ``lastExecution``) gates the composer freeze + the run-active
 * guard. The fresh-run visual clear must NOT happen here (an earlier attempt filtered this buffer and
 * reset the run-state mid-run, un-freezing the prompt bar); it lives at the render layer instead
 * (see ``visibleRunBlocks``).
 */
export function mergeRoomEnvelopes(historyEnvelopes, messages) {
  const seen = new Set();
  const out = [];
  const push = (env) => {
    if (!env || typeof env !== 'object') return;
    const id = env.eventId;
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    out.push(env);
  };
  (Array.isArray(historyEnvelopes) ? historyEnvelopes : []).forEach(push);
  (Array.isArray(messages) ? messages : []).forEach(push);
  return out;
}

/**
 * Which run blocks to render (single-run view).
 *
 * FRESH-RUN CLEAR: while THIS send is booting (``sending``), render NOTHING — the boot spinner + a
 * fresh graph take over on the USER event, so the prior run's block doesn't linger below the spinner
 * until the new run's AppSync events land. Once the new run's first live event clears ``sending``,
 * the single-run view resumes: only the newest run unless the user expanded "Load previous".
 */
export function visibleRunBlocks(allRunBlocks, { sending = false, showPrevious = false } = {}) {
  const blocks = Array.isArray(allRunBlocks) ? allRunBlocks : [];
  if (sending) return [];
  if (showPrevious || blocks.length <= 1) return blocks;
  return blocks.slice(-1);
}

/**
 * ASYNC PLAN-UPDATE beacon state, replay-safe (2026-09-20): the most recent of
 * ``PLAN_UPDATE_RUNNING`` / ``PLAN_UPDATED`` in the MERGED history+live buffer decides whether the
 * 'plan updating…' banner shows. The room previously derived this from the LIVE buffer only, so a
 * reload / WS reconnect while the async planner was still working (it runs for many minutes after
 * the run block ends) lost the beacon — the persisted history HAS the event, but the listener never
 * looked at it. Returns ``{ running, eventId }``; ``eventId`` de-dupes the side-effects (one
 * planStateBump per terminal beacon). Null when no plan beacon exists in the buffer.
 */
export function latestPlanBeacon(envelopes, currentRunId = null) {
  const list = Array.isArray(envelopes) ? envelopes : [];
  const cur = String(currentRunId || '').trim();
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const env = list[i];
    const et = typeof env?.eventType === 'string' ? env.eventType : env?.type;
    if (et !== 'PLAN_UPDATE_RUNNING' && et !== 'PLAN_UPDATED') continue;
    // OLD-EXECUTION MIXING (2026-09-21): the backend now stamps the run on the beacon
    // (correlationId + payload.runId). When we know the current run, IGNORE a beacon from a
    // DIFFERENT run so a stale PLAN_UPDATE_RUNNING/PLAN_UPDATED from a prior run/milestone can't
    // flip the 'plan updating…' state. A beacon with no run stamp (legacy) is still honored.
    const beaconRun = String(env?.correlationId || env?.executionId || env?.payload?.runId || '').trim();
    if (cur && beaconRun && beaconRun !== cur) continue;
    const eventId = env?.eventId
      || `${env?.ts || ''}:${et}:${env?.payload?.milestone || ''}`;
    return { running: et === 'PLAN_UPDATE_RUNNING', eventId, status: env?.payload?.status || '' };
  }
  return null;
}

/**
 * Re-entry guard: a room runs ONE task at a time. Block a new Send while this send is still booting
 * (``sending``) or a run is live (``uiState==='RUNNING'``) — belt-and-suspenders with the composer's
 * disabled Send, and it turns the server's 409 "a run is already in progress" into a local, friendly
 * rejection so a user who didn't notice the spinner can't fire a duplicate run.
 */
export function shouldBlockSend({ sending = false, uiState = null } = {}) {
  return Boolean(sending) || String(uiState || '').toUpperCase() === 'RUNNING';
}
